import React from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import {
  Flame, Star, Clapperboard, Calendar, LineChart, Zap, Globe,
  Swords, Map, Sparkles, Smile, Drama, Skull, BookOpen,
  Heart, Ghost, Music, Search as SearchIcon, Users, Bookmark, Trophy, MessageSquare, Crown, Download as DownloadIcon, Scroll, Scale,
  UserPlus, Send, ChevronDown
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import ApiStatusBadge from "@/components/ApiStatusBadge";
import UserOfTheWeek from "@/components/UserOfTheWeek";
import RecentSupporters from "@/components/RecentSupporters";
import api from "@/lib/api";
import { useEffect, useState } from "react";

const browse = [
  { key: "popular",     label: "Popular",     icon: Flame,       color: "#FF2A6D" },
  { key: "top_rated",   label: "Top Rated",   icon: Star,        color: "#FFD700" },
  { key: "now_playing", label: "Now Playing", icon: Clapperboard,color: "#22C77A" },
  { key: "upcoming",    label: "Upcoming",    icon: Calendar,    color: "#6D4AFF" },
];

const trending = [
  { key: "trending-week", label: "This Week",   icon: LineChart, color: "#00A8E1" },
  { key: "trending-day",  label: "Today",       icon: Zap,       color: "#FFD700" },
  { key: "movies-tv",     label: "Movies + TV", icon: Globe,     color: "#1CE783" },
];

const GENRE_ICONS = {
  Action:      { Icon: Swords,   color: "#EF4444" },
  Adventure:   { Icon: Map,      color: "#22C77A" },
  Animation:   { Icon: Sparkles, color: "#FFD700" },
  Comedy:      { Icon: Smile,    color: "#FFCE00" },
  Crime:       { Icon: Skull,    color: "#9CA3AF" },
  Documentary: { Icon: BookOpen, color: "#06B6D4" },
  Drama:       { Icon: Drama,    color: "#A855F7" },
  Family:      { Icon: Users,    color: "#FB7185" },
  Romance:     { Icon: Heart,    color: "#FF2A6D" },
  Horror:      { Icon: Ghost,    color: "#7C3AED" },
  Music:       { Icon: Music,    color: "#1CE783" },
};

function Section({ title, children, defaultOpen = true, alwaysOpen = false }) {
  const storageKey = `tt-sidebar-${title.toLowerCase().replace(/\s+/g, "-")}`;
  const [open, setOpen] = useState(() => {
    if (alwaysOpen) return true;
    try {
      const v = localStorage.getItem(storageKey);
      if (v === "1") return true;
      if (v === "0") return false;
    } catch { /* ignore */ }
    return defaultOpen;
  });
  const toggle = () => {
    if (alwaysOpen) return;
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(storageKey, next ? "1" : "0"); } catch { /* ignore */ }
  };
  return (
    <div className="mb-4">
      <button
        type="button"
        onClick={toggle}
        data-testid={`sidebar-section-${title.toLowerCase().replace(/\s+/g, "-")}`}
        className="w-full flex items-center justify-between px-4 mb-2 text-[10px] tracking-[0.2em] uppercase text-[var(--tt-text-faint)] font-semibold hover:text-white transition-colors group"
        aria-expanded={open}
      >
        <span>{title}</span>
        {!alwaysOpen && (
          <ChevronDown
            className={`w-3 h-3 transition-transform duration-200 ${open ? "rotate-0" : "-rotate-90"} group-hover:text-white`}
          />
        )}
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
      >
        <div className="overflow-hidden">
          <div className="space-y-1 px-3 pt-1">{children}</div>
        </div>
      </div>
    </div>
  );
}

function Item({ to, icon: Icon, label, active, testId }) {
  return (
    <NavLink
      to={to}
      data-testid={testId}
      className={() =>
        `flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-all cursor-pointer
         ${active
           ? "text-[var(--tt-primary)] bg-[rgba(255,42,109,0.1)] font-semibold"
           : "text-[var(--tt-text-muted)] hover:text-white hover:bg-white/5 font-medium"}`
      }
    >
      <Icon className="w-4 h-4" strokeWidth={2.25} />
      <span>{label}</span>
    </NavLink>
  );
}

export default function Sidebar({ genres = [] }) {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const current = sp.get("cat") || "popular";
  const currentGenre = sp.get("genre") || "";
  const currentProvider = sp.get("provider") || "";
  const currentNetwork = sp.get("network") || "";
  const currentCompany = sp.get("company") || "";
  const mediaType = sp.get("type") || "movie";
  const { user } = useAuth();

  const [studios, setStudios] = useState([]);
  const [providers, setProviders] = useState([]);
  const [networks, setNetworks] = useState([]);
  useEffect(() => {
    if (mediaType !== "movie") return;
    let cancel = false;
    Promise.all([
      api.get("/studios").then((r) => r.data).catch(() => []),
      api.get("/streaming-providers").then((r) => r.data).catch(() => []),
    ]).then(([s, p]) => {
      if (cancel) return;
      setStudios(s || []);
      setProviders(p || []);
    });
    return () => { cancel = true; };
  }, [mediaType]);
  useEffect(() => {
    if (mediaType !== "tv") return;
    let cancel = false;
    api.get("/tv-networks")
      .then(({ data }) => { if (!cancel) setNetworks(data || []); })
      .catch(() => { if (!cancel) setNetworks([]); });
    return () => { cancel = true; };
  }, [mediaType]);

  const platforms = mediaType === "tv" ? networks : providers;

  const go = (cat, extra = {}) => {
    const params = new URLSearchParams();
    params.set("cat", cat);
    Object.entries(extra).forEach(([k, v]) => v && params.set(k, v));
    // Preserve media type if we're already on one
    if (sp.get("type")) params.set("type", sp.get("type"));
    navigate(`/?${params.toString()}`);
  };

  return (
    <aside
      data-testid="sidebar-nav"
      className="hidden md:flex w-64 flex-shrink-0 h-full border-r border-[var(--tt-border)] bg-black/60 backdrop-blur-md flex-col overflow-y-auto py-6"
    >
      <div className="px-5 mb-8">
        <button
          type="button"
          onClick={() => navigate("/")}
          data-testid="sidebar-logo-home"
          aria-label="Go to home"
          className="relative block w-full text-left rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-[var(--tt-primary)] transition-transform hover:scale-[1.02] active:scale-100"
        >
          <span
            aria-hidden="true"
            className="absolute inset-0 -m-4 pointer-events-none"
            style={{
              background:
                "radial-gradient(closest-side, rgba(255,42,109,0.28), rgba(255,42,109,0.08) 55%, transparent 75%)",
              filter: "blur(6px)",
            }}
          />
          <img
            src="https://customer-assets.emergentagent.com/job_torrent-search-1/artifacts/rybfdplq_cooltext510704771142255.png"
            alt="TorHub"
            className="relative w-full max-w-[200px] h-auto select-none pointer-events-none drop-shadow-[0_0_14px_rgba(255,42,109,0.45)]"
            draggable={false}
          />
          <div className="relative text-[10px] text-[var(--tt-text-muted)] mt-1 tracking-wide pl-1">
            Movies & TV Shows
          </div>
        </button>
      </div>

      <div className="px-3 mb-5">
        <UserOfTheWeek />
        <RecentSupporters />
      </div>

      <Section title="Browse" alwaysOpen>
        {browse.map((b) => {
          const active = current === b.key && !sp.get("genre");
          return (
            <button
              key={b.key}
              onClick={() => go(b.key)}
              data-testid={`sidebar-nav-link-${b.key}`}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-all
                ${active
                  ? "text-[var(--tt-primary)] bg-[rgba(255,42,109,0.1)] font-semibold"
                  : "text-[var(--tt-text-muted)] hover:text-white hover:bg-white/5 font-medium"}`}
            >
              <b.icon className="w-4 h-4" strokeWidth={2.25} style={!active ? { color: b.color } : undefined} />
              <span>{b.label}</span>
            </button>
          );
        })}
      </Section>

      <Section title="Trending" defaultOpen={true}>
        {trending.map((t) => {
          const active = current === t.key;
          return (
            <button
              key={t.key}
              onClick={() => go(t.key)}
              data-testid={`sidebar-nav-link-${t.key}`}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-all
                ${active
                  ? "text-[var(--tt-primary)] bg-[rgba(255,42,109,0.1)] font-semibold"
                  : "text-[var(--tt-text-muted)] hover:text-white hover:bg-white/5 font-medium"}`}
            >
              <t.icon className="w-4 h-4" strokeWidth={2.25} style={!active ? { color: t.color } : undefined} />
              <span>{t.label}</span>
            </button>
          );
        })}
      </Section>

      {genres.length > 0 && (
        <Section title="Genres" defaultOpen={false}>
          {genres.slice(0, 12).map((g) => {
            const meta = GENRE_ICONS[g.name] || { Icon: SearchIcon, color: "#9CA3AF" };
            const Icon = meta.Icon;
            const active = String(g.id) === currentGenre;
            return (
              <button
                key={g.id}
                onClick={() => go("discover", { genre: g.id })}
                data-testid={`sidebar-genre-${g.name.toLowerCase()}`}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-all
                  ${active
                    ? "text-[var(--tt-primary)] bg-[rgba(255,42,109,0.1)] font-semibold"
                    : "text-[var(--tt-text-muted)] hover:text-white hover:bg-white/5 font-medium"}`}
              >
                <Icon className="w-4 h-4" strokeWidth={2.25} style={!active ? { color: meta.color } : undefined} />
                <span>{g.name}</span>
              </button>
            );
          })}
        </Section>
      )}

      {/* Streaming services (movies) / TV networks (tv) */}
      {platforms.length > 0 && (
        <Section title={mediaType === "tv" ? "Networks" : "Streaming"}>
          {platforms.map((p) => {
            const active = mediaType === "tv"
              ? String(p.id) === currentNetwork
              : String(p.id) === currentProvider;
            return (
              <button
                key={`${mediaType}-${p.id}`}
                onClick={() => go("discover", mediaType === "tv" ? { network: p.id } : { provider: p.id })}
                data-testid={`sidebar-${mediaType === "tv" ? "network" : "provider"}-${p.slug}`}
                className={`w-full flex items-center gap-3 px-3 py-1.5 rounded-full text-sm transition-all group relative
                  ${active
                    ? "text-white bg-white/8 font-semibold"
                    : "text-[var(--tt-text-muted)] hover:text-white hover:bg-white/5 font-medium"}`}
                style={active ? { boxShadow: `inset 0 0 0 1px ${p.accent}88` } : undefined}
                title={p.name}
              >
                <span
                  aria-hidden="true"
                  className="absolute left-0 inset-y-0 w-7 -ml-1 rounded-full opacity-0 group-hover:opacity-60 transition-opacity pointer-events-none blur-md"
                  style={{ background: p.accent }}
                />
                <span className="relative w-7 h-7 rounded-md overflow-hidden bg-white flex items-center justify-center flex-shrink-0">
                  {p.logo ? (
                    <img
                      src={p.logo}
                      alt={p.name}
                      loading="lazy"
                      className="w-full h-full object-contain p-0.5"
                      onError={(e) => {
                        e.target.style.display = "none";
                        if (e.target.nextSibling) e.target.nextSibling.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <span
                    className="w-full h-full text-[9px] font-extrabold text-black items-center justify-center"
                    style={{ display: p.logo ? "none" : "flex", background: p.accent, color: "#fff" }}
                  >
                    {p.name.slice(0, 3).toUpperCase()}
                  </span>
                </span>
                <span className="truncate">{p.name}</span>
                {active && (
                  <span
                    className="ml-auto w-1.5 h-1.5 rounded-full"
                    style={{ background: p.accent, boxShadow: `0 0 8px ${p.accent}` }}
                  />
                )}
              </button>
            );
          })}
        </Section>
      )}

      {/* Studios — production companies (movies only) */}
      {mediaType === "movie" && studios.length > 0 && (
        <Section title="Studios" defaultOpen={false}>
          {studios.map((s) => {
            const active = String(s.id) === currentCompany;
            return (
              <button
                key={`studio-${s.id}`}
                onClick={() => go("discover", { company: s.id })}
                data-testid={`sidebar-studio-${s.slug}`}
                className={`w-full flex items-center gap-3 px-3 py-1.5 rounded-full text-sm transition-all group relative
                  ${active
                    ? "text-white bg-white/8 font-semibold"
                    : "text-[var(--tt-text-muted)] hover:text-white hover:bg-white/5 font-medium"}`}
                style={active ? { boxShadow: `inset 0 0 0 1px ${s.accent}88` } : undefined}
                title={s.tmdb_name || s.name}
              >
                <span
                  aria-hidden="true"
                  className="absolute left-0 inset-y-0 w-7 -ml-1 rounded-full opacity-0 group-hover:opacity-60 transition-opacity pointer-events-none blur-md"
                  style={{ background: s.accent }}
                />
                <span className="relative w-7 h-7 rounded-md overflow-hidden bg-white flex items-center justify-center flex-shrink-0">
                  {s.logo ? (
                    <img
                      src={s.logo}
                      alt={s.name}
                      loading="lazy"
                      className="w-full h-full object-contain p-0.5"
                      onError={(e) => {
                        e.target.style.display = "none";
                        if (e.target.nextSibling) e.target.nextSibling.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <span
                    className="w-full h-full text-[9px] font-extrabold items-center justify-center"
                    style={{ display: s.logo ? "none" : "flex", background: s.accent, color: "#fff" }}
                  >
                    {s.name.slice(0, 3).toUpperCase()}
                  </span>
                </span>
                <span className="truncate">{s.name}</span>
                {active && (
                  <span
                    className="ml-auto w-1.5 h-1.5 rounded-full"
                    style={{ background: s.accent, boxShadow: `0 0 8px ${s.accent}` }}
                  />
                )}
              </button>
            );
          })}
        </Section>
      )}

      {user && (
        <Section title="My Lists" defaultOpen={true}>
          <button
            onClick={() => navigate("/watchlist")}
            data-testid="sidebar-watchlist"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-all text-[var(--tt-text-muted)] hover:text-white hover:bg-white/5 font-medium"
          >
            <Bookmark className="w-4 h-4" strokeWidth={2.25} style={{ color: "#FFD700" }} />
            <span>Watchlist</span>
          </button>
          <button
            onClick={() => navigate("/favorites")}
            data-testid="sidebar-favorites"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-all text-[var(--tt-text-muted)] hover:text-white hover:bg-white/5 font-medium"
          >
            <Heart className="w-4 h-4" strokeWidth={2.25} style={{ color: "#FF2A6D" }} />
            <span>Favorites</span>
          </button>
          <button
            onClick={() => navigate(`/u/${user.user_id}`)}
            data-testid="sidebar-profile"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-all text-[var(--tt-text-muted)] hover:text-white hover:bg-white/5 font-medium"
          >
            <Users className="w-4 h-4" strokeWidth={2.25} style={{ color: "#06B6D4" }} />
            <span>My Profile</span>
          </button>
          <button
            onClick={() => navigate("/friends")}
            data-testid="sidebar-friends"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-all text-[var(--tt-text-muted)] hover:text-white hover:bg-white/5 font-medium"
          >
            <UserPlus className="w-4 h-4" strokeWidth={2.25} style={{ color: "#1CE783" }} />
            <span>Friends</span>
          </button>
          <button
            onClick={() => navigate("/messages")}
            data-testid="sidebar-messages"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-all text-[var(--tt-text-muted)] hover:text-white hover:bg-white/5 font-medium"
          >
            <Send className="w-4 h-4" strokeWidth={2.25} style={{ color: "#9D4CDD" }} />
            <span>Messages</span>
          </button>
          <button
            onClick={() => navigate("/downloads")}
            data-testid="sidebar-downloads"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-all text-[var(--tt-text-muted)] hover:text-white hover:bg-white/5 font-medium"
          >
            <DownloadIcon className="w-4 h-4" strokeWidth={2.25} style={{ color: "#FF2A6D" }} />
            <span>Downloads</span>
            {user?.tier !== "premium" && (
              <span className="ml-auto text-[9px] uppercase tracking-wider bg-[var(--tt-primary)]/20 text-[var(--tt-primary)] px-1.5 py-0.5 rounded font-bold">£10</span>
            )}
          </button>
        </Section>
      )}

      <Section title="Community" defaultOpen={true}>
        <button
          onClick={() => navigate("/forums")}
          data-testid="sidebar-forums"
          className="w-full flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-all text-[var(--tt-text-muted)] hover:text-white hover:bg-white/5 font-medium"
        >
          <MessageSquare className="w-4 h-4" strokeWidth={2.25} style={{ color: "#FF2A6D" }} />
          <span>Forums</span>
        </button>
        <button
          onClick={() => navigate("/leaderboard")}
          data-testid="sidebar-leaderboard"
          className="w-full flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-all text-[var(--tt-text-muted)] hover:text-white hover:bg-white/5 font-medium"
        >
          <Trophy className="w-4 h-4" strokeWidth={2.25} style={{ color: "#FFC845" }} />
          <span>Leaderboard</span>
        </button>
        <button
          onClick={() => navigate("/hall-of-fame")}
          data-testid="sidebar-hall-of-fame"
          className="w-full flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-all text-[var(--tt-text-muted)] hover:text-white hover:bg-white/5 font-medium"
        >
          <Crown className="w-4 h-4" strokeWidth={2.25} style={{ color: "#FFD700" }} />
          <span>Hall of Fame</span>
        </button>
        <button
          onClick={() => navigate("/chat")}
          data-testid="sidebar-chat"
          className="w-full flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-all text-[var(--tt-text-muted)] hover:text-white hover:bg-white/5 font-medium"
        >
          <MessageSquare className="w-4 h-4" strokeWidth={2.25} style={{ color: "#22C77A" }} />
          <span>Lounge Chat</span>
        </button>
      </Section>

      <Section title="Tools" defaultOpen={false}>
        <button
          onClick={() => navigate("/software")}
          data-testid="sidebar-software"
          className="w-full flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-all text-[var(--tt-text-muted)] hover:text-white hover:bg-white/5 font-medium"
        >
          <DownloadIcon className="w-4 h-4" strokeWidth={2.25} style={{ color: "#1CE783" }} />
          <span>BitTorrent Clients</span>
        </button>
      </Section>

      <Section title="About" defaultOpen={false}>
        <button
          onClick={() => navigate("/rules")}
          data-testid="sidebar-rules"
          className="w-full flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-all text-[var(--tt-text-muted)] hover:text-white hover:bg-white/5 font-medium"
        >
          <Scroll className="w-4 h-4" strokeWidth={2.25} style={{ color: "#A855F7" }} />
          <span>Rules</span>
        </button>
        <button
          onClick={() => navigate("/disclaimer")}
          data-testid="sidebar-disclaimer"
          className="w-full flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-all text-[var(--tt-text-muted)] hover:text-white hover:bg-white/5 font-medium"
        >
          <Scale className="w-4 h-4" strokeWidth={2.25} style={{ color: "#06B6D4" }} />
          <span>Disclaimer</span>
        </button>
      </Section>

      {user && (user.role === "admin" || user.role === "mod") && (
        <div className="mt-auto px-3 pb-2">
          <button
            onClick={() => navigate("/admin")}
            data-testid="admin-dashboard-link"
            className="w-full text-left px-3 py-2 rounded-full text-sm font-semibold bg-[var(--tt-yellow)]/15 text-[var(--tt-yellow)] hover:bg-[var(--tt-yellow)]/25 transition-colors"
          >
            {user.role === "admin" ? "⚙ Admin Panel" : "🛡 Mod Panel"}
          </button>
        </div>
      )}

      <div className={`px-3 pb-3 ${user && (user.role === "admin" || user.role === "mod") ? "" : "mt-auto pt-3"}`}>
        <ApiStatusBadge />
      </div>
    </aside>
  );
}

